import { defineStore } from 'pinia';

export const useDesignerStore = defineStore('designerStore', {
    state: () => ({
        designerMode: 0,
        loading: true,
        variantLoading: false,
        disableCart: false,
        editing: false,
        itemId: null,
        itemType: 'cart',
        renderView: "Exterior",
        mobileModal: false,
        viewing: false,
        pricing: {
            price: 0,
            shippingPrice: 0,
            shippingFreeMin: 0,
        },
        activeTab: "all",
        cartMessage: "",
        cartMessageType: "success",
        addedToCart: false,
        product: {
            object: {},
            variants: [],
            layers: [],
            dimensionFields: [],
            linkedProducts: [],
            tabs: {},
            tabLayers: {},
            activeLayerTab: {},
            completedTabs: []
        },
        branding: {},
        helpArticle: '',
        description: '',
        selectedVariant: {},
        selectedOptions: {
            variantId: null,
            qty: 1,
            'variant-width': null,
            'variant-height': null
        },
        images: {},
        // Flow data storage
        flowData: {
            flowId: null,
            flowUuid: null,
            steps: [],  // Array to store completed steps
            formData: {},  // Object to store all form data by step ID
            questionAnswers: []  // Array to store question steps with answers
        }
    }),
    getters: {},
    actions: {
        /**
         * Moves to the next tab.
         * If Last tab then do nothing
         */
        nextTab() {
            // Save the current active tab if not already saved
            if (!this.product.completedTabs.includes(this.activeTab)) {
                this.product.completedTabs.push(this.activeTab)
            }

            // Determine the index of the current tab
            const currentIndex = this.product.tabs.indexOf(this.activeTab)
            const nextIndex = currentIndex + 1

            if (nextIndex < this.product.tabs.length) {
                // There is a next tab: update the active tab
                this.activeTab = this.product.tabs[nextIndex]
            }
        },
        /**
         * Moves to the previous tab.
         * Returns true if it successfully moved to a previous tab,
         * or false if the current tab is the first tab.
         */
        previousTab() {
            const currentIndex = this.product.tabs.indexOf(this.activeTab)
            if (currentIndex > 0) {
                // Move to the previous tab
                this.activeTab = this.product.tabs[currentIndex - 1]
                return true
            } else {
                // No previous tab exists
                return false
            }
        },
        swapView() {
            this.renderView = this.renderView === "Exterior" ? "Interior" : "Exterior";
        },
        setPrice(price) {
            this.pricing.price = price
        },
        incrementQty() {
            this.selectedOptions.qty++;
        },
        decrementQty() {
            if (this.selectedOptions.qty > 1) this.selectedOptions.qty--;
        },
        setSelectedOptions(options, setVariant = true) {
            // Merge options with existing to preserve default structure
            this.selectedOptions = {
                ...this.selectedOptions,
                ...options
            }

            if (setVariant && this.selectedVariant && this.selectedVariant.id){
                this.selectedOptions.variantId = this.selectedVariant.id
            }
        },
        setLayers(layers) {
            this.product.layers = layers
        },
        setLinkedProducts(linkedProducts) {
            this.product.linkedProducts = linkedProducts
        },
        setDimensionFields(fields) {
            this.product.dimensionFields = fields
        },
        changeOption(layer, option) {
            this.selectedOptions[layer.id] = option.id
        },
        setProduct(product) {
          this.product.object = product
        },
        setVariants(variants) {
            this.product.variants = variants
        },
        setTabs(tabs) {
            this.product.tabs = tabs;
        },
        setImages(images) {
            this.images = images
        },
        setHelp(help) {
            this.helpArticle = help
        },
        setLoading(loading) {
            this.loading = loading
        },
        addToCart() {
            this.cartMessage = "Product added to cart.";
            setTimeout(() => {
                this.cartMessage = "";
            }, 3000);
        },
        setVariant(variant) {
            this.selectedOptions.variantId = variant.id;
            this.selectedVariant = variant
        },
        setBranding(branding) {
            this.branding = branding
        },
        // Flow-related actions
        initFlow(flowUuid, flowId = null) {
            this.flowData = {
                flowId: flowId,
                flowUuid: flowUuid,
                steps: [],
                formData: {},
                questionAnswers: []
            };
        },
        saveFormData(stepId, stepName, formData) {
            // Store form data indexed by step ID
            this.flowData.formData[stepId] = {
                stepId: stepId,
                stepName: stepName,
                data: { ...formData },
                timestamp: new Date().toISOString()
            };
            
            // Track that this step was completed
            if (!this.flowData.steps.find(s => s.stepId === stepId)) {
                this.flowData.steps.push({
                    stepId: stepId,
                    stepName: stepName,
                    type: 'form',
                    completed: true
                });
            }
        },
        saveQuestionAnswer(stepId, stepName, question, answer) {
            // Store question and answer
            this.flowData.questionAnswers.push({
                stepId: stepId,
                stepName: stepName,
                question: question,
                answer: answer,  // 'yes' or 'no'
                timestamp: new Date().toISOString()
            });
            
            // Track that this step was completed
            if (!this.flowData.steps.find(s => s.stepId === stepId)) {
                this.flowData.steps.push({
                    stepId: stepId,
                    stepName: stepName,
                    type: 'question',
                    completed: true
                });
            }
        },
        getFlowDataForSubmission() {
            // Prepare all flow data for API submission
            return {
                flowId: this.flowData.flowId,
                flowUuid: this.flowData.flowUuid,
                completedSteps: this.flowData.steps,
                formData: this.flowData.formData,
                questionAnswers: this.flowData.questionAnswers,
                submittedAt: new Date().toISOString()
            };
        },
        clearFlowData() {
            this.flowData = {
                flowId: null,
                flowUuid: null,
                steps: [],
                formData: {},
                questionAnswers: []
            };
        },
        resetStore() {
            this.product = {
                object: {},
                variants: [],
                    layers: [],
                    dimensionFields: [],
                    linkedProducts: [],
                    tabs: {},
                tabLayers: {},
                activeLayerTab: {},
                completedTabs: []
            };

            this.selectedOptions = {};
            this.selectedVariant = {};
            this.images = {};
            this.helpArticle = '';
            this.branding = {};
            this.share = {
                loading: true,
                token: ''
            }
            this.pricing = {}
            this.cartMessage = "";
            this.cartMessageType = "success";
            this.addedToCart = false;
            this.editing = false;
            this.itemId = null;
            this.itemType = 'cart';
            this.renderView = "Exterior";
            this.activeTab = "all";
            this.variantLoading = false;
            this.loading = true;
            
            // Reset flow data
            this.clearFlowData();
        }
    },
});
